import type { ReactNode } from 'react';
import { createFileRoute, redirect, useRouter } from '@tanstack/react-router';
import z from 'zod';

import { OAuthConsentPage } from '@/components/features/oauth-consent/OAuthConsentPage';
import { PlainLayout } from '@/modus/PlainLayout';
import { Wordmark } from '@/modus/Wordmark';
import { type ConsentRequestLookup, getConsentRequestServerFn } from '@/server/auth/oauth-consent';
import { getUserInfo } from '@/server/user';
import { Button } from '@/ui/Button';

// Deliberately not under `_public`: that layout redirects anyone holding a session to `/`,
// and a signed-in user is exactly who this page is for — approving an agent is a click, not
// another login. An unauthenticated visitor is sent to the existing /login and comes back.
export const Route = createFileRoute('/oauth-consent')({
  staticData: {
    public: true,
  },
  validateSearch: z.object({
    request_id: z.string().optional(),
  }),
  loaderDeps: ({ search }) => ({ request_id: search.request_id }),
  loader: async ({ deps, location }) => {
    const requestId = deps.request_id;
    const missing: ConsentRequestLookup = { status: 'expired' };
    if (!requestId) return { requestId: null, lookup: missing, userEmail: null };

    // getUserInfo throws a redirect to /login (carrying redirectTo) when there is no session,
    // so the sign-in leg is the app's existing one rather than a second implementation.
    const { user } = await getUserInfo({ data: { redirectTo: location.href } });
    if (!user?.email) throw redirect({ to: '/login', search: { redirectTo: location.href } });

    const lookup = await getConsentRequestServerFn({
      data: { request_id: requestId },
    });
    return { requestId, lookup, userEmail: user.email };
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { requestId, lookup, userEmail } = Route.useLoaderData();
  const router = useRouter();

  if (lookup.status === 'unavailable') {
    return (
      <ConsentNotice title="Could not load this request">
        <p className="text-sm text-white/60">Something went wrong on our side. Try again in a moment.</p>
        <Button variant="secondary" className="mt-2 w-full" onClick={() => router.invalidate()}>
          Retry
        </Button>
      </ConsentNotice>
    );
  }

  if (!requestId || !userEmail || lookup.status !== 'ok') {
    return (
      <ConsentNotice title="This request has expired">
        <p className="text-sm text-white/60">
          Authorization requests are short-lived. Start the connection again from the application.
        </p>
      </ConsentNotice>
    );
  }

  return (
    <OAuthConsentPage
      requestId={requestId}
      clientName={lookup.request.client_name}
      redirectHost={lookup.request.redirect_host}
      userEmail={userEmail}
    />
  );
}

function ConsentNotice({ title, children }: { title: string; children: ReactNode }) {
  return (
    <PlainLayout main className="flex min-h-screen items-center justify-center bg-tlai-0 px-6">
      <div className="flex w-full max-w-sm flex-col items-center">
        <Wordmark size={180} className="mb-8" />
        <div className="flex w-full flex-col gap-2 rounded-2xl bg-tlai-3 p-6">
          <h1 className="text-lg font-medium text-white">{title}</h1>
          {children}
        </div>
      </div>
    </PlainLayout>
  );
}
